// Supabase Realtime subscription for the notifications table.
// Prerequisite: Realtime must be enabled for the `notifications` table
// (ALTER PUBLICATION supabase_realtime ADD TABLE notifications;).
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Subscribes to Supabase Realtime INSERT events on the `notifications` table,
 * filtered by the current user's ID, and invalidates React Query caches
 * (notifications, borrowed items, loan requests, items) so the bell badge
 * and list update immediately without waiting for the 30-second poll.
 *
 * Must be mounted exactly once in the app (e.g. via NotificationRealtimeSync
 * in App.tsx). The 30-second polling fallback in `useNotificationCount` is
 * kept as a safety net for dropped Realtime connections.
 */
export function useNotificationRealtime() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user) return;

    // Live updates via WebSocket — fires on new notification row
    const channel = supabase
      .channel(`notifications:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ['notifications', user.id] });
          queryClient.invalidateQueries({ queryKey: ['notifications', 'count', user.id] });
          queryClient.invalidateQueries({ queryKey: ['my-borrowed-items'] });
          queryClient.invalidateQueries({ queryKey: ['loan-requests'] });
          // Loan-related notifications (approved, returned, overdue, proposals)
          // change item status/holder — refresh items on this client too.
          queryClient.invalidateQueries({ queryKey: ['items'] });
          queryClient.invalidateQueries({ queryKey: ['my-items'] });
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, queryClient]);
}
